/**
 * preview-pool-filter — pure helpers to scope preview items to one path
 * allocation pool (default paths / a conditional group / a Binom rule).
 *
 * Path items carry their pool key in `parentId`; landers/offers reference
 * their owning path via `pathId`, so they are scoped through the path index.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowLayer,
  FlowOptimizerItemPreview,
  FlowOptimizerPool,
  FlowOptimizerPreviewResult,
} from '@trafficpulse/shared';

/** Map path itemId -> its pool key, built from the paths layer. */
export function buildPathPoolIndex(
  paths: ReadonlyArray<FlowOptimizerItemPreview>,
): Map<string, string> {
  const index = new Map<string, string>();
  for (const path of paths) index.set(path.itemId, path.parentId ?? '');
  return index;
}

/** Scope a layer bucket to one pool; `pool === null` means "all pools". */
export function filterItemsByPool(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
  layer: FlowLayer,
  pool: string | null,
  pathPools: ReadonlyMap<string, string>,
): FlowOptimizerItemPreview[] {
  if (pool == null) return [...items];
  if (layer === 'paths') return items.filter((i) => i.parentId === pool);
  return items.filter(
    (i) => i.pathId != null && pathPools.get(i.pathId) === pool,
  );
}

/**
 * Pools available for the selector. Prefers the backend-provided list
 * (default first, display names); falls back to the distinct pool keys on
 * path items for payloads predating the `pools` field.
 */
export function poolOptionsFromResult(
  result: FlowOptimizerPreviewResult,
): FlowOptimizerPool[] {
  if (result.pools && result.pools.length > 0) return result.pools;
  const seen = new Map<string, FlowOptimizerPool>();
  for (const item of result.items) {
    if (item.layer !== 'paths' || !item.parentId) continue;
    if (!seen.has(item.parentId)) {
      seen.set(item.parentId, { id: item.parentId, name: item.parentId });
    }
  }
  return [...seen.values()];
}
